// Fortress market / deck balance data. Types live in `types/fortress.ts`.
//
// The card catalogue is transcribed from the physical "Fortress Cards" sheet.
// Two decks: the dark cards are the `win` deck (victory-condition fortresses),
// the light cards are the `REGULAR` deck (effect fortresses). Costs are paid in
// Obsidian, so `cost` is `[0, 0, 0, n]`. `winTiming` is set only where the card
// carries an explicit timing badge — EOR → "end-of-round", SOT → "start-of-turn".
//
// Patterns were MACHINE-EXTRACTED from the "Fortress Cards" PDF's vector art
// (each glyph snapped to the honeycomb lattice), so node placements are exact.
// For cards whose art shows two orientations either side of a divider, the
// left/top diagram is transcribed — the other side is its mirror image, which
// the matcher (and `MirrorPair` in the client) derives.
// Building shapes: circle = scout, triangle = barracks, square = mine,
// star = wild (matches any one building). The art's "X" glyphs are `markers`:
// empty nodes the Fortress occupies on completion. Thick bars on edges are
// `bridges`: Bridge pieces that are part of the pattern.
const EDGE = {
    NE: { q: 2, r: -1, s: -1 },
    NW: { q: -1, r: -1, s: 2 },
    S: { q: -1, r: 2, s: -1 },
    N: { q: 1, r: -2, s: 1 },
    SE: { q: 1, r: 1, s: -2 },
    SW: { q: -2, r: 1, s: 1 },
};
const A_DIRS = new Set(["NE", "NW", "S"]); // legal from an A node; rest from B
/** Walk a honeycomb path from the anchor (an A node) and return the resulting
 * node-vector offset. Steps must alternate sublattices; an illegal step throws,
 * which catches a mis-authored path at module load. */
function walk(path) {
    let v = { q: 0, r: 0, s: 0 };
    let onA = true;
    for (const d of path) {
        if (A_DIRS.has(d) !== onA) {
            throw new Error(`Illegal step ${d} from ${onA ? "A" : "B"} node in path [${path.join(",")}]`);
        }
        const e = EDGE[d];
        v = { q: v.q + e.q, r: v.r + e.r, s: v.s + e.s };
        onA = !onA;
    }
    return v;
}
/** A building at a path from the anchor. `b("wild", "NE")` = a wild one step NE. */
const b = (shape, ...at) => ({
    shape,
    at,
});
/** A Bridge on the edge crossed by the path's LAST step: `br("S")` = the edge
 * from the anchor to its S neighbour; earlier steps just walk to the edge. */
const br = (...at) => ({ at });
/** Assemble a pattern from building specs (+ marker and bridge paths). */
function pat(cells, markers = [], bridges = []) {
    const buildings = cells.map((c) => ({
        shape: c.shape,
        offset: walk(c.at),
    }));
    return {
        buildings,
        markers: markers.map((m) => walk(m)),
        bridges: bridges.map(({ at }) => {
            if (at.length === 0)
                throw new Error("bridge path needs >= 1 step");
            return [walk(at.slice(0, -1)), walk(at)];
        }),
    };
}
// --- Card factory ---------------------------------------------------------
function card(kind, id, name, obsidian, description, pattern, winTiming) {
    return {
        id,
        kind,
        name,
        description,
        cost: [0, 0, 0, obsidian],
        pattern,
        ...(winTiming ? { winTiming } : {}),
    };
}
const win = (id, name, obsidian, description, pattern, winTiming) => card("win", id, name, obsidian, description, pattern, winTiming);
const regular = (id, name, obsidian, description, pattern) => card("REGULAR", id, name, obsidian, description, pattern);
/** The Win (victory-condition) deck — the dark cards. */
const winCards = [
    win("enchanted-mines", "Enchanted Mines", 14, "Add a token to this card when you build a mine on a resource that is not in the lowest energy level. Remove a token when any of your mines is destroyed. Win when you have 7 tokens on this card. Tokens are removed at the start of each round.", pat([
        b("mine", "NE"),
        b("mine"),
        b("mine", "NE", "SE"),
    ], [
        ["S"],
        ["NE", "SE", "S"],
        ["S", "SE"],
        ["S", "SE", "S"],
    ])),
    win("piercing-needle", "Piercing Needle", 19, "Win game. All buildings in this fortress must be built in the highest energy zone.", pat([b("barracks"), b("wild", "S", "SE")])),
    win("underhand-skyscraper", "Underhand Skyscraper", 8, "Win the game if you have a barracks in the highest energy zone. This fortress can only be built if all your buildings are in the highest energy zone.", pat([
        b("wild", "NW"),
        b("wild", "NE"),
        b("wild"),
    ], [
        ["NE", "N", "NW", "N"],
        ["NW", "N"],
        ["NE", "N"],
        ["S"],
    ], [
        br("NE", "N", "NW", "N"),
    ]), "end-of-round"),
    win("miner-bots", "Miner Bots", 17, "Win the game. This fortress can only be built if you have at least one mine in every energy zone.", pat([
        b("mine", "NE", "N", "NW"),
        b("wild"),
        b("mine", "S"),
    ], [
        ["NW", "N"],
        ["NE", "N"],
        ["NW"],
        ["NE"],
    ])),
    win("vacmakd", "Vacmak'd", 11, "Place 1 token on this card after anyone destroys someone else's building from a barracks attack. Remove 1 token if anyone successfully defends a barracks attack. Win the game at 8 tokens. You cannot have less than 0 tokens.", pat([
        b("barracks"),
        b("barracks", "S"),
        b("wild", "S", "SE", "S", "SW"),
    ], [
        ["NW"],
        ["NE"],
        ["S", "SW"],
        ["S", "SE"],
    ])),
    win("giant-sphincter", "Giant Sphincter", 13, "Whenever more than 3 buildings are destroyed in a round, place a token on this card. Win at 3 tokens.", pat([
        b("barracks"),
        b("wild", "S", "SE"),
        b("mine", "S", "SE", "S"),
    ], [
        ["S"],
        ["S", "SW"],
        ["S", "SW", "S"],
        ["S", "SW", "S", "SW"],
    ])),
    win("artifact-of-speed", "Artifact of Speed", 2, "Win game if 4 artifacts are built by you. Gain 1 mineral.", pat([b("scout", "S")], [[]], [br("S")])),
    win("artifact-of-courage", "Artifact of Courage", 2, "Win game if 4 artifacts are built by you. Gain 1 food.", pat([b("barracks", "S")], [[]], [br("S")])),
    win("artifact-of-knowledge", "Artifact of Knowledge", 2, "Win game if 4 artifacts are built by you. Gain 1 energy.", pat([b("mine", "S")], [[]], [br("S")])),
    win("artifact-of-plebians", "Artifact of Plebians", 2, "Win game if 4 artifacts are built by you. Gain 1 obsidian.", pat([b("wild"), b("wild", "S")], [["NE"]])),
    win("artifact-of-atrocity", "Artifact of Atrocity", 2, "Win game if 4 artifacts are built by you. Gain 1 of any resource.", pat([], [[]], [br("NW"), br("NE")])),
    win("traveling-booba", "Traveling Booba", 7, "This must be built entirely in the lowest energy zone. Once per round, you may move this fortress by your barracks attack range. Moving takes energy based on the highest energy zone that you have moved into. Once completely in the highest energy zone, you win. This fortress may be destroyed and collect one-time resources.", pat([b("scout"), b("scout", "S")]), "start-of-turn"),
    win("bursted-flap", "Bursted Flap", 5, "After building in an energy zone, place energy resource(s) on this card corresponding to the energy penalty of that zone. Also add energy resources for other energy-requiring actions. If there are 10 or more resources at the end of the round, place a token on this card. Win at 3 tokens. Remove all energy resources at the end of the round.", pat([
        b("scout"),
        b("mine", "NW", "SW", "S"),
        b("wild", "S", "SW"),
    ], [
        ["NW", "N"],
        ["NW"],
        ["S"],
        ["S", "SE"],
    ], [
        br("S"),
    ])),
    win("obsidian-pipeline-masterworks", "Obsidian Pipline Masterworks", 11, "Win game. Can only be built if you have 6 or more fortresses under your control.", pat([
        b("wild", "S"),
        b("wild", "NE", "SE", "NE", "SE", "S"),
        b("wild", "NE", "SE", "NE", "SE", "NE", "SE", "S"),
    ], [
        ["NE", "SE", "NE", "SE", "S", "SE"],
    ], [
        br("NE", "SE", "NE", "SE", "S", "SE"),
        br("NE", "SE", "NE", "SE", "S", "SE", "NE"),
    ])),
    win("parasitic-breakfast", "Parasitic Breakfast", 14, "Win if you have a barracks that is not a part of a fortress, within your attack range to 3 unique opponent fortresses.", pat([
        b("wild", "NW", "N", "NW"),
        b("barracks", "NE"),
        b("wild"),
    ], [
        ["NE", "N", "NW", "N", "NW"],
        ["NW", "N", "NW", "N"],
        ["NE", "N", "NW", "N"],
    ]), "start-of-turn"),
    win("inverse-molehill", "Inverse Molehill", 5, "Win condition fortresses cost 5 Obsidian less for you (minimum 1 Obsidian cost).", pat([
        b("wild"),
    ], [
        ["NE", "N", "NW", "N"],
        ["NE", "N", "NW"],
        ["NE", "N"],
        ["NE"],
    ], [
        br("NE", "N", "NE"),
        br("NE"),
    ])),
    win("obsidian-blimp-port", "Obsidian Blimp-port", 3, "Buildings in this fortress may be destroyed. If destroyed, this fortress is placed back into the pile. As long as this fortress is intact, add a token to this card at the end of the round. Win the game if there are 2 tokens on this card. This fortress must have at least 1 building in highest energy zone.", pat([
        b("scout"),
        b("scout", "NE", "SE", "NE", "SE", "NE", "SE"),
    ], [
        ["NE"],
        ["NE", "SE", "NE", "SE", "NE"],
    ])),
    win("penguin-puncher", "Penguin Puncher", 13, "Place a token on this fortress at the beginning of the round when you are the first player to take an action. Once this reaches 2 tokens, win the game.", pat([
        b("wild"),
        b("wild", "S", "SW"),
        b("wild", "S", "SW", "S"),
        b("wild", "S", "SE", "S", "SW"),
    ], [
        ["S"],
        ["S", "SE"],
        ["S", "SE", "S"],
        ["S", "SE", "S", "SW", "S"],
    ])),
    win("cg-builder-of-bridges", "CG Builder of Bridges", 14, "Win the game as soon as an entire energy zone has all their nodes connected. Nodes can be connected by bridges or by the natural connections on a tile. Nodes do not have to be connected across different energy levels.", pat([
        b("wild"),
    ], [
        ["NE", "N", "NW"],
        ["NW"],
        ["NE"],
        ["S"],
    ], [
        br("NW", "N"),
        br("NE", "N"),
        br("S"),
    ])),
    win("big-sprawler", "Big Sprawler", 17, "Win the game if you occupy 80/X tiles, where X equals the number of players there are, rounded up. Tiles only count as being occupied if the buildings on them are connected to your home base.", pat([
        b("wild"),
        b("wild", "NE", "SE", "NE", "SE", "NE", "SE"),
        b("scout", "S", "SE", "S"),
    ], [
        ["NE"],
        ["NE", "SE", "NE", "SE", "NE"],
        ["NE", "SE", "NE", "SE"],
    ], [
        br("NE", "SE", "S"),
    ]), "start-of-turn"),
    win("mirror-morse", "Mirror Morse", 14, "Win the game if you build a mine on a starting node in the starting tile opposite to your own starting tile.", pat([
        b("scout", "S"),
        b("mine", "NE", "SE", "S"),
        b("wild", "NE", "SE", "NE", "SE", "S"),
    ], [
        ["NE", "SE"],
        ["S", "SE"],
        ["NE", "SE", "S", "SE"],
    ]), "end-of-round"),
    win("exodial-the-forsaken-one", "Exodial the Forsaken One", 24, "Obliterate. May be built using other player's buildings.", pat([
        b("scout", "NE", "N", "NW"),
        b("wild"),
        b("mine", "NW", "SW", "S"),
        b("barracks", "NE", "SE", "S"),
    ], [], [
        br("NW"),
        br("NE"),
        br("S"),
    ])),
    win("bankman", "Bankman", 14, "Win game if you have 9 mineral, 9 food and 9 energy in your bank.", pat([
        b("wild"),
        b("mine", "NE", "SE"),
    ], [
        ["NE"],
        ["NE", "SE", "NE"],
        ["S"],
        ["NE", "SE", "S"],
    ], [
        br("S"),
        br("NE", "SE", "S"),
    ]), "start-of-turn"),
    win("plink-harvester", "Plink Harvester", 12, "Win the game when you have 9 obsidian production and 9 obsidian banked.", pat([
        b("mine"),
        b("wild", "S", "SE"),
        b("wild", "S", "SE", "S"),
    ], [
        ["NE"],
        ["NE", "SE"],
        ["S"],
        ["S", "SE", "S", "SW"],
    ], [
        br("S"),
    ]), "end-of-round"),
    win("booba-farmer", "Booba Farmer", 11, "Win if you have more of a resource type banked than the combined total of the same resource type banked by all other players.", pat([
        b("mine", "NE"),
        b("wild"),
    ], [
        ["NE", "N", "NE"],
        ["NE", "N"],
        ["NW"],
        ["S"],
    ], [
        br("NE", "N", "NE", "N"),
    ]), "start-of-turn"),
];
/** The Regular (effect) deck — the light cards. */
const regularCards = [
    regular("godspeed-machine", "Godspeed Machine", 2, "If your scout is about to be destroyed by a barracks, you may place a token on this card instead to protect it. You may not exceed 2 tokens. Clear all tokens at the end of the round.", pat([
        b("scout", "S"),
        b("barracks", "S", "SE", "S", "SW", "S"),
    ], [
        ["S", "SW"],
        ["S", "SW", "S"],
        ["S", "SE", "S", "SW"],
    ])),
    regular("bridgette-the-beauty", "Bridgette the Beauty", 2, "Bridges may be built with any resource.", pat([
        b("mine", "S"),
    ], [
        ["NW", "SW", "S"],
        ["S", "SW"],
        ["S", "SE"],
    ], [
        br("S", "SW"),
        br("S", "SE"),
    ])),
    regular("hole-worm", "Hole Worm", 1, "After building this fortress, you make place this exact same fortress anywhere else in the same energy level as this one. There is no cost to this action. Flip this card over when you do so.", pat([], [], [br("NW"), br("NE"), br("S")])),
    regular("skippity-star", "Skippity Star", 7, "Double all banked resources once per game. Flip this card over once it's effect has been used. This takes up 1 action.", pat([
        b("mine"),
        b("mine", "NE", "SE"),
    ], [
        ["NE", "N"],
        ["NE"],
        ["S"],
        ["NE", "SE", "S"],
    ])),
    regular("clobbering-bat", "Clobbering Bat", 3, "Gain +1 Obsidian at the beginning of the round for each fortress you have constructed.", pat([
        b("mine"),
        b("wild", "S", "SW"),
    ], [
        ["NW", "SW", "NW"],
        ["NW", "SW"],
        ["NW", "SW", "S"],
    ], [
        br("NW", "SW", "NW"),
    ])),
    regular("resource-sequencer", "Resource Sequencer", 4, "Your mines are 1 mineral cheaper. Also gain +1 of each resource each round if you have the lowest mineral production in the game.", pat([
        b("wild", "NW"),
        b("mine"),
        b("wild", "NE", "SE", "S"),
    ], [
        ["NW", "N"],
        ["NE"],
        ["NW", "SW"],
        ["S"],
    ])),
    regular("energized-bun-bun", "Energized Bun-Bun", 3, "When gaining one-time resources, you may double the amount you receive. Place a token on this card every time it's effect has been used. You may not exceed 5 tokens.", pat([
        b("wild"),
        b("wild", "NW", "SW", "S"),
    ], [
        ["NW"],
        ["NE"],
        ["S"],
        ["NW", "SW", "S", "SW"],
    ])),
    regular("conversion-treadmill", "Conversion Treadmill", 4, "Your obsidian can be used as any other resource.", pat([
        b("mine", "NW"),
        b("wild"),
    ], [
        ["NW", "N", "NW"],
        ["NW", "N"],
        ["NW", "SW", "NW"],
        ["NW", "SW"],
    ])),
    regular("sudoku-bear", "Sudoku Bear", 4, "Immediately reset all your production to 0/0/0/0, no matter what your mines are built on. Gain 10/10/10/10 resources. You cannot have negative production if your mines are destroyed when you have 0 production.", pat([
        b("barracks"),
        b("scout", "S", "SE"),
        b("wild", "S", "SE", "S"),
    ], [
        ["NW"],
        ["S"],
        ["S", "SE", "S", "SE"],
    ], [
        br("S", "SE"),
    ])),
    regular("plasmamagnetic-waves", "Plasmamagnetic Waves", 5, "Energy penalties are decreased from 1/3/5 to 1/2/3.", pat([
        b("wild"),
        b("scout", "S", "SW", "S"),
        b("wild", "S", "SW", "S", "SW", "S", "SW"),
    ], [
        ["NE"],
        ["NE", "SE"],
        ["S", "SW", "S", "SW", "S", "SW", "S"],
        ["S", "SE", "S", "SW", "S", "SW", "S", "SW"],
    ])),
    regular("swole-turtle", "Swole Turtle", 3, "Gain +1 energy when collecting one-time energy resources. If your buildings are in a lower energy zone than all other players, gain +2.", pat([
        b("wild", "S"),
        b("barracks", "S", "SE", "S"),
    ], [
        ["S", "SE"],
        ["NE", "SE", "S", "SE", "S"],
        ["S", "SE", "S", "SE"],
    ], [
        br("S"),
    ])),
    regular("kinetic-absorption-facility", "Kinetic Absorption Facility", 4, "You may place a token on this card instead of paying energy penalties. You cannot exceed 4 tokens.", pat([
        b("wild"),
        b("barracks", "S"),
        b("wild", "S", "SE", "S", "SW", "S"),
    ], [
        ["NW"],
        ["NE"],
        ["NW", "SW"],
        ["NE", "SE"],
    ])),
    regular("transmutation-bench", "Transmutation Bench", 3, "Food and energy are completely interchangable. Cannot be built in the lowest energy level.", pat([
        b("wild"),
    ], [
        ["NE", "N", "NW"],
        ["NW", "N"],
        ["NE", "SE", "S"],
        ["S", "SE"],
    ], [
        br("NW", "SW"),
        br("S", "SW"),
    ])),
    regular("telescopic-arm", "Telescopic Arm", 3, "Gain +2 attack range. You may attack through opponent fortresses and barracks'. You may untap 2 barracks per round.", pat([
        b("barracks", "NW"),
        b("barracks"),
        b("wild", "NE", "SE", "S"),
    ], [
        ["NW", "N", "NW"],
        ["NW", "N"],
        ["NW", "SW", "NW"],
        ["NE"],
        ["NW", "SW"],
        ["S"],
    ])),
    regular("porkypine-neck", "Porkypine Neck", 2, "Gain +2 defend range. You may untap 2 barracks per round.", pat([
        b("barracks", "NE", "N", "NW"),
        b("wild"),
    ], [
        ["NE", "N", "NW", "N"],
        ["NW", "N", "NW"],
        ["NE", "N", "NE"],
        ["NW", "N"],
        ["NE", "N"],
    ], [
        br("NE", "N", "NW", "N"),
    ])),
    regular("icarlie", "iCarlie", 2, "If a player builds within your defence range to this fortress, gain 2 resources of any kind.", pat([
        b("barracks"),
        b("wild", "NW", "SW", "S"),
    ], [
        ["NW"],
        ["NE"],
        ["S"],
        ["NW", "SW", "S", "SW"],
    ])),
    regular("dungeon-dice-martyr", "Dungeon Dice Martyr", 3, "Draw 2 regular & 2 win condition fortress. Only you see these cards and can build them. They are all 1 obsidian cheaper. Whenever an opponent's action causes your building(s) to be destroyed, the attacker chooses one of your face down cards to return to the discard pile.", pat([
        b("mine", "NE"),
        b("wild", "NE", "SE", "NE"),
        b("wild"),
        b("wild", "NE", "SE", "NE", "SE"),
    ], [
        ["NE", "N", "NE"],
        ["NE", "N"],
        ["NE", "SE"],
    ])),
    regular("supreme-bondage", "Supreme Bondage", 1, "Lock or unlock one fortress card as an action. Locked cards can't be cycled but remain purchasable by anyone. Max 2 locked cards at any given time.", pat([
        b("scout", "S"),
        b("wild", "S", "SE", "S", "SW", "S"),
    ], [
        ["NW", "SW", "S"],
        ["S", "SW"],
        ["S", "SW", "S"],
        ["S", "SE", "S", "SW"],
    ], [
        br("S", "SE", "S", "SW", "S"),
    ])),
    regular("cyclopz", "Cyclopz", 3, "Perform card cycle actions without any cost or action penalty. Place a token on this card when it's effect is used. You cannot exceed 3 tokens.", pat([
        b("wild"),
        b("wild", "NE", "SE"),
        b("barracks", "S", "SE", "S"),
    ], [
        ["S", "SW", "S"],
        ["NE", "SE", "S", "SE", "S"],
        ["S", "SE", "S", "SW"],
        ["S", "SE", "S", "SE"],
    ])),
    regular("curious-clit", "Curious Clit", 3, "Combine all discarded and visible fortresses in a row and shuffle. Glance and the top 10 fortresses and re-arrange them in any order you want. Add a token to this card when this effect is taken. You may not exceed 3 tokens.", pat([
        b("wild", "NW"),
        b("wild"),
        b("barracks", "NW", "SW", "S"),
    ], [
        ["S"],
        ["S", "SW"],
        ["S", "SE"],
        ["S", "SE", "S"],
    ])),
    regular("action-blaster", "Action Blaster", 2, "All players may take 3 actions per turn. You may take 4 actions per turn. You may also exceed 4 actions per turn by paying 1 energy per additional action.", pat([
        b("wild", "NW", "N", "NW"),
        b("wild"),
    ], [
        ["NW", "N", "NW", "N", "NW"],
        ["NW", "N", "NW", "N"],
        ["NE"],
        ["NE", "SE"],
    ], [
        br("NW", "N"),
    ])),
    regular("times-up", "Times Up", 2, "You may end the current round immediately and take the first turn marker. Place a token on this card when you take this action. You may not exceed 2 tokens. Your end of round win fortresses takes precedence over end of round win fortresses.", pat([
        b("wild", "S"),
        b("wild", "S", "SW", "S"),
        b("wild", "S", "SE", "S"),
    ], [
        [],
        ["S", "SW"],
        ["S", "SE"],
        ["S", "SE", "S", "SW", "S"],
    ], [
        br("S", "SE", "S", "SW", "S"),
    ])),
    regular("to-the-moon-tokenizer-9000", "To the moon Tokenizer 9000", 3, "Once per round, add or remove a token from any one of your fortresses. Flip this card over when it's effect is used. Flip this card back face-up at the beginning of the round.", pat([
        b("wild", "NE"),
        b("wild"),
        b("wild", "NE", "SE", "S", "SE"),
    ], [
        ["NE", "SE", "S"],
        ["S", "SE"],
        ["S", "SE", "S"],
    ])),
    regular("trifecta-sphinx", "Trifecta Sphinx", 2, "Your buildings have +2 building range.", pat([
        b("mine", "NE", "N", "NW"),
        b("wild"),
    ], [
        ["NE", "N", "NE"],
        ["NE", "N"],
        ["NE"],
    ], [
        br("NE", "N"),
    ])),
    regular("rabid-shoes", "Rabid Shoes", 3, "Your scouts and bridges have +3 building range and can be built through fortresses and barracks'.", pat([
        b("scout"),
        b("wild", "S", "SE"),
    ], [
        ["S"],
        ["NE", "SE", "S"],
        ["NE", "SE", "S", "SE"],
    ])),
];
export const FORTRESS_CARDS_CONFIG = {
    regularMarketSize: 5,
    winMarketSize: 5,
    cycleCostPerRegularCard: [0, 1, 0, 0],
    cycleCostPerWinCard: [0, 1, 0, 0],
    regularCards,
    winCards,
};
//# sourceMappingURL=fortress.js.map